import packageJson from "../../package.json";

// Typed environment accessor for Vite (HashRouter CSR app)
// Blueprint rule: never read import.meta.env outside this module.

export type AppEnv = "dev" | "staging" | "prod" | string;

type EnvShape = {
  PROD: boolean;
  VITE_APP_ENV?: string;
  VITE_APP_VERSION?: string;
  VITE_API_BASE_URL?: string;
};

type PublicEnvKey = Exclude<keyof EnvShape, "PROD">;

// Internal helper: normalize and guard against undefined.
function readEnv(): EnvShape {
  // `import.meta.env` is Vite-injected; narrow its shape here.
  const env = (
    import.meta as unknown as {
      env: Record<string, string | boolean | undefined>;
    }
  ).env;
  return {
    PROD: Boolean(env.PROD),
    VITE_APP_ENV:
      typeof env.VITE_APP_ENV === "string" ? env.VITE_APP_ENV : undefined,
    VITE_APP_VERSION:
      typeof env.VITE_APP_VERSION === "string"
        ? env.VITE_APP_VERSION
        : undefined,
    VITE_API_BASE_URL:
      typeof env.VITE_API_BASE_URL === "string"
        ? env.VITE_API_BASE_URL
        : undefined,
  };
}

const envCache = readEnv();

/**
 * Return the current app environment (dev/staging/prod/other).
 * Defaults to "dev" in local development and "prod" for production builds if unset.
 */
export function getAppEnv(): AppEnv {
  const raw = (envCache.VITE_APP_ENV ?? "").trim().toLowerCase();
  if (raw === "production" || raw === "prod") return "prod";
  if (raw === "staging" || raw === "stg") return "staging";
  if (raw === "development" || raw === "dev") return "dev";
  return raw || (envCache.PROD ? "prod" : "dev");
}

/**
 * Return the app version, preferring an explicit env override; otherwise falls back to package.json.
 */
export function getAppVersion(): string {
  const envVersion = (envCache.VITE_APP_VERSION ?? "").trim();
  if (envVersion) return envVersion;
  return packageJson.version || "0.0.0";
}

/**
 * Return the configured API base URL (may be undefined in mock mode).
 */
export function getApiBaseUrl(): string | undefined {
  const baseRaw =
    envCache.VITE_API_BASE_URL?.trim() ||
    (getAppEnv() === "dev" ? "http://127.0.0.1:3000" : undefined);
  if (!baseRaw) return undefined;
  if (baseRaw.endsWith("/client")) {
    return baseRaw;
  }
  return `${baseRaw.replace(/\/$/, "")}/client`;
}

/**
 * Safe accessor to retrieve an env value by key (prefixed only).
 */
export function getEnv(key: PublicEnvKey): string | undefined {
  return envCache[key]?.trim() || undefined;
}
